import { useCallback } from 'react'
import { useAuthContext } from '../context/AuthContext'
import { useGlobalState } from 'context/GlobalStateContext'
import { PermissionItem, resolveValueOrArray, ValueOrArray } from 'shared'

/**
 * returns a reactive callback which resolves whether the current user has
 * permission to do something in the current project
 *
 * @see {@link useGlobalState}
 * @see {@link useAuthContext}
 * @see {@link PermissionItem}
 */
function useProjectAuthorized() {
  const {
    state: { projectGlobalId },
  } = useGlobalState()
  const { user } = useAuthContext()
  return useCallback<
    (permission: ValueOrArray<PermissionItem> | 'admin') => boolean
  >(
    (permission) =>
      (permission === 'admin'
        ? user?.admin(projectGlobalId).authorized
        : user?.someAuthorized(resolveValueOrArray(permission), projectGlobalId)
            .authorized) ?? false,
    [user, projectGlobalId]
  )
}

/**
 * returns a boolean describing whether the current user has permission to
 * do something
 *
 * @see {@link useProjectAuthorized}
 */
export function useCheckPermission(
  permissions: ValueOrArray<PermissionItem> | 'admin'
) {
  const authorized = useProjectAuthorized()
  return authorized(permissions)
}

/**
 * Permission types which don't require an associated record
 */
export type SimplePermissions =
  | undefined
  | 'admin'
  | ValueOrArray<PermissionItem>
  | boolean

/**
 * A permission type which requires a record to resolve
 */
type PermissionCallback<T> = (record: T) => boolean

/**
 * @see {@link SimplePermissions}
 * @see {@link PermissionCallback}
 */
export type Permissions<T> = SimplePermissions | PermissionCallback<T>

export interface WithPermissions<T> {
  /**
   * options for setting permissions
   *
   * @see {@link Permissions}
   */
  permission: Permissions<T>
}

/**
 * returns a reactive callback which resolves a provided permissions type into a
 * boolean describing whether the current user has permission to do something
 *
 * for most simple situations, use {@link useCheckPermission}
 *
 * @example
 * const authorized = useCheckPermissions(record)
 *
 * if (authorized(undefined)) ...
 * if (authorized(({ field }) => {
 *   if (field !== null) return true
 *   return true
 * })) ...
 * else if (authorized(Permission.EditExample)) ...
 * else if (authorized([Permission.EditExample, Permission.ViewExample])) ...
 * else if (authorized(someBoolean)) ...
 */
export function useCheckPermissions<T = unknown>(preloadedRecord?: T | T[]) {
  const authorized = useProjectAuthorized()
  return useCallback(
    (permission: Permissions<T>, record?: T | T[]) => {
      if (permission === undefined) return false
      else if (Array.isArray(permission)) return authorized(permission)
      else if (typeof permission === 'boolean') return permission
      else if (typeof permission === 'function') {
        const resolved = preloadedRecord ?? record
        if (!resolved) return false
        return resolveValueOrArray(resolved).some(permission)
      } else return authorized(permission)
    },
    [authorized, preloadedRecord]
  )
}

/**
 * returns a tuple where the first item is a map where the key is the supplied
 * {@link UseCheckPermissionsMapOptions.mapKey|mapKey} and the value is whether
 * the current user has the permission associated with the key
 *
 * the second item is whether any of the values in the map is true
 *
 * @param options {@link UseCheckPermissionsMapOptions}
 *
 * @example
 * const [map, someAllowed] = useCheckPermissionMap([...].map(({ permissions }, key) => ({
 *   key,
 *   permissions,
 *   record: typeof permissions === 'function' ? record : undefined
 * })))
 *
 * @see {@link UseCheckPermissionsMap}
 * @see {@link UseCheckPermissionsMapItem}
 */
export function useCheckPermissionsMap<T = unknown, U = string | number>(
  items: Array<UseCheckPermissionsMapItem<T, U>>
): UseCheckPermissionsMap<U> {
  const check = useCheckPermissions<T>()
  const map = items.reduce((map, item) => {
    const key = item.key
    let authorized = false
    if (typeof item.permission === 'function') {
      const { permission: permissions, record } =
        item as CheckCallbackPermissionsItem<T>
      authorized = check(permissions, record ?? [])
    } else authorized = check(item.permission)
    return map.set(key, authorized)
  }, new Map<U, boolean>())
  return [map, [...map.values()].some((v) => v)]
}

type UseCheckPermissionsMap<T> = [Map<T, boolean>, boolean]

interface CheckSimplePermissionsItem {
  permission: SimplePermissions
}

interface CheckCallbackPermissionsItem<T> {
  permission: PermissionCallback<T>
  record: ValueOrArray<T>
}

/**
 * @see {@link useCheckPermissionsMap}
 * @see {@link UseCheckPermissionsMap}
 * @see {@link CheckCallbackPermissionsItem}
 * @see {@link CheckSimplePermissionsItem}
 */
export type UseCheckPermissionsMapItem<T, U> = { key: U } & (
  | CheckSimplePermissionsItem
  | CheckCallbackPermissionsItem<T>
)
